import { Injectable } from '@angular/core';

import { Action, Store } from '@ngrx/store';
import { Actions, createEffect, ofType, OnInitEffects } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { filter, map, take, tap } from 'rxjs';

import { GlobalEventsService } from '@kros-sk/shared/utils/global-events';

import { deviceDetectorActions } from './device-detector.actions';
import { DeviceDetectorService } from '../../utils/device-detector.service';
import { fromDeviceDetector } from './device-detector.selectors';
import { NativeAppBridgeService } from '../../utils/native-app-bridge.service';

@Injectable()
export class DeviceDetectorEffects implements OnInitEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly globalEvents: GlobalEventsService,
    private readonly deviceDetector: DeviceDetectorService,
    private readonly store: Store,
    private readonly nativeAppBridge: NativeAppBridgeService
  ) {}

  readonly initialDetectDevice$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(deviceDetectorActions.initialized),
      map(() => this.deviceDetector.detect()),
      map((detectedDevice) => deviceDetectorActions.detected({ detectedDevice }))
    );
  });

  readonly detectNativeAppAfterFirstDeviceDetection$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(deviceDetectorActions.detected),
        take(1), // ensure the effect runs only once
        tap(() => {
          // this detection process may or may not be async, so it is just side effect, and it
          // dispatches actions on its own...
          this.nativeAppBridge.detectNativeAppType();
        })
      );
    },
    { dispatch: false }
  );

  readonly detectResizeRelated$ = createEffect(() => {
    return this.globalEvents.listenEvent('window:resize').pipe(
      concatLatestFrom(() => [
        this.store.select(fromDeviceDetector.selectDeviceType),
        this.store.select(fromDeviceDetector.selectIsTabletBreakpoint),
      ]),
      map(([_, currentDeviceType, currentIsTabletBreakpoint]) => ({
        currentDeviceType,
        currentIsTabletBreakpoint,
        detectedDevice: this.deviceDetector.detectDeviceType(),
      })),
      filter(
        ({ currentDeviceType, currentIsTabletBreakpoint, detectedDevice }) =>
          currentDeviceType !== detectedDevice.deviceType || currentIsTabletBreakpoint !== detectedDevice.isTabletBreakpoint
      ),
      map(({ detectedDevice }) =>
        deviceDetectorActions.detected({
          detectedDevice: {
            deviceType: detectedDevice.deviceType,
            isTabletBreakpoint: detectedDevice.isTabletBreakpoint,
          },
        })
      )
    );
  });

  ngrxOnInitEffects(): Action {
    return deviceDetectorActions.initialized();
  }
}
